import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  getBacklogStories, getAllStory, getAllSprints,
  getAllUsers, assignStoryToSprint, moveStoryToBacklog,
  updateBacklogOrder,
} from "../../../pages/api/backlog";
import { postSprintTeam, updateSprint, deleteSprint, startSprint } from "../../../pages/api/sprint";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../../asset/css/styles.css";
import TaskDetails from "../../activesprint/taskdetails/page";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CreateTaskModal from "./CreateTaskModal/page";

/* ── Badge colour maps ──────────────────────────────────────────────────────── */
const PRIORITY_COLORS = { high: "#e53935", medium: "#fb8c00", low: "#43a047" };
const PRIORITY_BG     = { High: "#fdecea", Medium: "#fff3e0", Low: "#e8f5e9" };
const TYPE_STYLES = {
  feature:     { background: "#e3f0ff", color: "#1565c0" },
  bug:         { background: "#fdecea", color: "#c62828" },
  task:        { background: "#e8f5e9", color: "#2e7d32" },
  enhancement: { background: "#fff3e0", color: "#e65100" },
  report:      { background: "#f3e5f5", color: "#6a1b9a" },
};

const getPriorityColor = (p) => PRIORITY_COLORS[p?.toLowerCase()] || "#aaa";
const getTypeBadgeStyle = (t) => TYPE_STYLES[t?.toLowerCase()] || { background: "#e0e0e0", color: "#555" };

const toLabel = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "";
const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "--";

/* ── SVG icons (reused) ─────────────────────────────────────────────────────── */
const IconSearch   = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>;
const IconFilter   = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>;
const IconChevDown = () => <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>;
const IconCheck    = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#1d4ed8" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>;
const IconPlus     = ({ color = "#fff" }) => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const IconPlay     = () => <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><polygon points="5 3 19 12 5 21 5 3"/></svg>;
const IconEdit     = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4f76c8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>;
const IconTrash    = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M9 6V4h6v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>;
const IconCalendar = () => <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
const IconChevRight = ({ expanded }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"
    style={{ flexShrink: 0, cursor: "pointer", transform: expanded ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);
const IconStack = () => <i className="bi bi-stack" />;
const IconBull  = () => <i className="bi bi-bullseye" />;
const IconSpeed = () => <i className="bi bi-speedometer2" />;
const IconList  = () => <i className="bi bi-list-check" />;

/* ── Shared Modal shell ─────────────────────────────────────────────────────── */
const ModalShell = ({ onClose, title, children, maxWidth = "520px" }) => (
  <div className="pb-modal-overlay" onClick={onClose}>
    <div className="pb-modal-box" style={{ width: "90%", maxWidth, maxHeight: "90vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()}>
      <div className="pb-modal-header">
        <span className="pb-modal-title">{title}</span>
        <button className="pb-modal-close" onClick={onClose}>×</button>
      </div>
      {children}
    </div>
  </div>
);

/* ── Create Sprint Modal ─────────────────────────────────────────────────────── */
const CreateSprintModal = ({ show, onClose, onSprintCreated, token, sprints = [] }) => {
  const [sprintName, setSprintName] = useState("");
  const [startDate,  setStartDate]  = useState("");
  const [endDate,    setEndDate]    = useState("");
  const [nameError,  setNameError]  = useState("");
  const [dateError,  setDateError]  = useState("");
  const [endDateError, setEndDateError] = useState("");
  const [isSaving,   setIsSaving]   = useState(false);

  const reset = () => {
    setSprintName(""); setStartDate(""); setEndDate("");
    setNameError(""); setDateError(""); setEndDateError("");
    
  };
  const handleClose = () => { reset(); onClose(); };

  const handleSave = async () => {
    let err = false;
    if (!sprintName.trim()) { setNameError("Sprint Name is required."); err = true; } else setNameError("");
if (!startDate) {
      setDateError("Start Date is required.");
      err = true;
    } else {
      setDateError("");
    }  if (!endDate)           { setEndDateError("End Date is required."); err = true; }
    else if (endDate < startDate) { setEndDateError("End Date cannot be before Start Date."); err = true; }
    else setEndDateError("");
    if (err) return;

    setIsSaving(true);
    try {
const res = await postSprintTeam({ sprintName: sprintName.trim(), startDate, endDate, sprintStatus: "PLANNED" }, token);
      if (res.status === 200) {
        toast.success("Sprint created successfully!");
        onSprintCreated?.();
        handleClose();
      } else {
        setNameError(res.message || "Failed to create sprint.");
      }
    } catch (e) {
      setNameError(e.response?.data?.message || "An error occurred.");
    } finally {
      setIsSaving(false);
    }
  };

  if (!show) return null;
  return (
    <ModalShell title="Create Sprint" onClose={handleClose}>
      <div className="pb-modal-body">
        
<div>
          <label className="pb-modal-label">SPRINT NAME <span className="pb-modal-required">*</span></label>
          <input className="pb-modal-input" placeholder="Enter name" value={sprintName}
            onChange={(e) => { setSprintName(e.target.value); setNameError(""); }} />
          {nameError && <div className="pb-modal-error">{nameError}</div>}
        </div>

        <div>
          <label className="pb-modal-label">SPRINT GOAL (OPTIONAL)</label>
          <input className="pb-modal-input" placeholder="Enter goal" />
        </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
          <div style={{ flex: "1 1 45%", minWidth: 140 }}>
            <label className="pb-modal-label">START DATE <span className="pb-modal-required">*</span></label>
           <input
  type="date"
  className="pb-modal-input"
  value={startDate}
min={new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10)}
  onChange={(e) => { setStartDate(e.target.value); setDateError(""); setEndDate(""); setEndDateError(""); }}
    onClick={(e) => e.target.showPicker?.()}

/>
            {dateError && <div className="pb-modal-error">{dateError}</div>}
          </div>
                    <div style={{ flex: "1 1 45%", minWidth: 140 }}>
            <label className="pb-modal-label">END DATE <span className="pb-modal-required">*</span></label>
            <input type="date" className="pb-modal-input" value={endDate} min={startDate || ""}
  onChange={(e) => { setEndDate(e.target.value); setEndDateError(""); }}
  onClick={(e) => {
    if (!startDate) { setEndDateError("Please select Start Date first."); e.preventDefault(); return; }
    e.target.showPicker?.();
  }} />
            {endDateError && <div className="pb-modal-error">{endDateError}</div>}
          </div>
        </div>

        <div>
          <label className="pb-modal-label">TEAM CAPACITY (STORY POINTS)</label>
          {/* width stays inline — unique narrow override */}
          <input type="text" className="pb-modal-input" style={{ width: "140px" }} defaultValue={10} min={1} />
        </div>
      </div>

      <div className="pb-modal-footer">
        <button className="pb-btn-cancel" onClick={handleClose}>Cancel</button>
        <button className="pb-btn-submit" onClick={handleSave} disabled={isSaving}
          style={{ opacity: isSaving ? 0.7 : 1 }}>
          {isSaving ? "Creating..." : "Create Sprint"}
        </button>
      </div>
    </ModalShell>
  );
};

/* ── Edit Sprint Modal ──────────────────────────────────────────────────────── */
const EditSprintModal = ({ show, onClose, onUpdated, token, sprint }) => {
  const [sprintName, setSprintName] = useState("");
  const [startDate,  setStartDate]  = useState("");
  const [endDate,    setEndDate]    = useState("");
  const [nameError,  setNameError]  = useState("");
  const [dateError,  setDateError]  = useState("");
  const [endDateError, setEndDateError] = useState("");
  const [isSaving,   setIsSaving]   = useState(false);

  useEffect(() => {
    if (sprint) {
      setSprintName(sprint.sprintName || "");
      setStartDate(sprint.startDate?.slice(0, 10) || "");
      setEndDate(sprint.endDate?.slice(0, 10) || "");
      setNameError(""); setDateError(""); setEndDateError("");
    }
  }, [sprint]);

  const handleSave = async () => {
    let err = false;
    if (!sprintName.trim()) { setNameError("Sprint Name is required."); err = true; } else setNameError("");
    if (!startDate)         { setDateError("Start Date is required.");  err = true; } else setDateError("");
    if (!endDate)           { setEndDateError("End Date is required."); err = true; }
    else if (endDate < startDate) { setEndDateError("End Date cannot be before Start Date."); err = true; }
    else setEndDateError("");
    if (err) return;

    setIsSaving(true);
    try {
      const res = await updateSprint(
        { id: sprint.id, sprintName: sprintName.trim(), startDate, endDate }, token,
      );
      if (res.status === 200) {
        toast.success("Sprint updated successfully!");
        onUpdated?.();
        onClose();
      } else {
        setNameError(res.message || "Failed to update sprint.");
      }
    } catch (e) {
      setNameError(e.response?.data?.message || "An error occurred.");
    } finally {
      setIsSaving(false);
    }
  };

  if (!show || !sprint) return null;
  return (
    <ModalShell title="Edit Sprint" onClose={onClose} maxWidth="480px">
      <div className="pb-modal-body">
        <div>
          <label className="pb-modal-label">SPRINT NAME <span className="pb-modal-required">*</span></label>
          <input className="pb-modal-input" placeholder="Sprint name" value={sprintName}
            onChange={(e) => { setSprintName(e.target.value); setNameError(""); }} />
          {nameError && <div className="pb-modal-error">{nameError}</div>}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
          <div style={{ flex: "1 1 45%", minWidth: 140 }}>
            <label className="pb-modal-label">START DATE <span className="pb-modal-required">*</span></label>
            <input type="date" className="pb-modal-input" value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setDateError(""); setEndDate(""); setEndDateError(""); }} />
            {dateError && <div className="pb-modal-error">{dateError}</div>}
          </div>
          <div style={{ flex: "1 1 45%", minWidth: 140 }}>
            <label className="pb-modal-label">END DATE <span className="pb-modal-required">*</span></label>
            <input type="date" className="pb-modal-input" value={endDate} min={startDate || ""}
              onChange={(e) => { setEndDate(e.target.value); setEndDateError(""); }}
              onClick={(e) => { if (!startDate) { setEndDateError("Please select Start Date first."); e.preventDefault(); } }} />
            {endDateError && <div className="pb-modal-error">{endDateError}</div>}
          </div>
        </div>
      </div>

      <div className="pb-modal-footer">
        <button className="pb-btn-cancel" onClick={onClose}>Cancel</button>
        <button className="pb-btn-submit" onClick={handleSave} disabled={isSaving}
          style={{ opacity: isSaving ? 0.7 : 1 }}>
          {isSaving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </ModalShell>
  );
};

/* ── Delete Sprint Modal ────────────────────────────────────────────────────── */
const DeleteSprintModal = ({ sprintId, onClose, onDeleted, token }) => {
  if (!sprintId) return null;

  const handleDelete = async () => {
    try {
      const res = await deleteSprint(sprintId, token);
      console.log("Delete response:", res);
      toast.success("Sprint deleted. Tasks moved back to backlog.");
      onDeleted?.();
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error
        || err?.response?.data || err?.message || "Failed to delete sprint.";
      toast.error(String(msg));
    }
  };

  return (
    <ModalShell title="Delete Sprint" onClose={onClose} maxWidth="400px">
      <div className="pb-modal-body">
        <p style={{ fontSize: "14px", color: "#374151", margin: 0 }}>
          Are you sure you want to delete this sprint? This action cannot be undone.
        </p>
      </div>
      <div className="pb-modal-footer">
        <button className="pb-btn-cancel" onClick={onClose}>Cancel</button>
        <button className="pb-btn-delete" onClick={handleDelete}>Delete</button>
      </div>
    </ModalShell>
  );
};
const StartSprintModal = ({ sprint, tasks, onClose, onStarted, token }) => {
  const [isStarting, setIsStarting] = useState(false);

  if (!sprint) return null;

const totalPoints = tasks.reduce((s, x) => s + (Number(x.storyPoints) || 0) + (Number(x.testStoryPoints) || 0), 0);

  const handleStart = async () => {
    setIsStarting(true);
    try {
      await startSprint(sprint.id, token);
      toast.success(`${sprint.sprintName} started successfully!`);
      onStarted?.();
      onClose();
    } catch (err) {
      const msg = err?.response?.data || "Failed to start sprint.";
      toast.error(typeof msg === "string" ? msg : "Failed to start sprint.");
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <ModalShell title="Start Sprint" onClose={onClose} maxWidth="480px">
      <div className="pb-modal-body">
        <p style={{ fontSize: "14px", color: "#374151", marginBottom: 16 }}>
          Are you sure you want to start <strong>{sprint.sprintName}</strong>?
        </p>
        <div style={{ background: "#f9fafb", borderRadius: 8, padding: "12px 16px", marginBottom: 12, border: "1px solid #e5e7eb" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <span style={{ fontSize: 13, color: "#6b7280" }}>Duration</span>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{formatDate(sprint.startDate)} – {formatDate(sprint.endDate)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <span style={{ fontSize: 13, color: "#6b7280" }}>Total Tasks</span>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{tasks.length}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ fontSize: 13, color: "#6b7280" }}>Story Points</span>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{totalPoints}</span>
          </div>
        </div>
        {tasks.length > 0 && (
          <div style={{ maxHeight: 180, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
            {tasks.map((t) => (
              <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 13 }}>
                <span style={{ color: "#9ca3af" }}>#{t.id}</span>
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.storyTitle}</span>
{(Number(t.storyPoints) > 0 || Number(t.testStoryPoints) > 0) && <span style={{ color: "#6b7280", flexShrink: 0 }}>{(Number(t.storyPoints) || 0) + (Number(t.testStoryPoints) || 0)} SP</span>}
              </div>
            ))}
          </div>
        )}
        {tasks.length === 0 && (
          <p style={{ fontSize: 13, color: "#f59e0b", margin: 0 }}>⚠ No tasks in this sprint yet.</p>
        )}
      </div>
      <div className="pb-modal-footer">
        <button className="pb-btn-cancel" onClick={onClose}>Cancel</button>
        <button className="pb-btn-submit" onClick={handleStart} disabled={isStarting}
          style={{ opacity: isStarting ? 0.7 : 1 }}>
          {isStarting ? "Starting..." : "Start Sprint"}
        </button>
      </div>
    </ModalShell>
  );
};

/* ════════════════════════════════════════════════════════════════════════════ */
const PlanningBacklogTab = ({ refreshKey }) => {
  const [stories,          setStories]          = useState([]);
  const [,                 setStory]            = useState([]);
  const [loading,          setLoading]          = useState(false);
  const [currentStory,     setCurrentStory]     = useState(null);
  const [showViewTaskModal,setShowViewTaskModal] = useState(false);
  const [searchText,       setSearchText]       = useState("");
  const [activeFilter,     setActiveFilter]     = useState("All");
  const [sprints,          setSprints]          = useState([]);
  const [showFilterDropdown,setShowFilterDropdown] = useState(false);
  const [showCreateTask,   setShowCreateTask]   = useState(false);
  const [showCreateSprint, setShowCreateSprint] = useState(false);
  const [refreshSprintsKey,setRefreshSprintsKey]= useState(0);
  const [showEditSprint,   setShowEditSprint]   = useState(false);
  const [editingSprint,    setEditingSprint]    = useState(null);
const [deletingSprintId, setDeletingSprintId] = useState(null);
  const [startingSprint, setStartingSprint] = useState(null);
  const [isStarting, setIsStarting] = useState(false);
    const [currentPage,      setCurrentPage]      = useState(1);
  const [dragOverSprintId, setDragOverSprintId] = useState(null);
  const [allUsers,         setAllUsers]         = useState([]);
  const [sprintStories,    setSprintStories]    = useState({});
  const [expandedSprints,  setExpandedSprints]  = useState({});
 const [sprintTaskPage,   setSprintTaskPage]   = useState({});
const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [backlogOrder, setBacklogOrder] = useState([]);

  const persistBacklogOrder = (order) => {
    setBacklogOrder(order);
    // Saves the new sequence to the DB — same order then shows for every
    // user/browser/device, since it's read from the story's backlogOrder column.
    updateBacklogOrder(order, token).catch(() => {
      toast.error("Failed to save new order");
    });
  };

  const sprintsPerPage   = 5;
  const tasksPerSprintPage = 5;
const filterRef        = useRef(null);
  const backlogScrollRef = useRef(null);
  const sprintColRef     = useRef(null);
  const token      = localStorage.getItem("token");
  const userRoleId  = Number(localStorage.getItem("roleId"));
  const isAdmin     = userRoleId === 1 || userRoleId === 2;
  const isTeamLead  = userRoleId === 6;
  const canDragTasks = isAdmin || isTeamLead; 

  /* ── Responsive ── */
  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;

  /* ── Mobile drag-scroll ── */
  useEffect(() => {
    if (!isMobile) return;
    let timer = null;
    const onDragOver = (e) => {
      e.preventDefault();
      const { clientY } = e;
      const h = window.innerHeight;
      if (timer) clearInterval(timer);
      if (clientY < 80)       timer = setInterval(() => window.scrollBy(0, -8), 16);
      else if (clientY > h - 80) timer = setInterval(() => window.scrollBy(0, 8), 16);
    };
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
    document.addEventListener("dragover", onDragOver);
    document.addEventListener("dragend",  stop);
    document.addEventListener("drop",     stop);
    return () => {
      document.removeEventListener("dragover", onDragOver);
      document.removeEventListener("dragend",  stop);
      document.removeEventListener("drop",     stop);
      stop();
    };
  }, [isMobile]);

  /* ── Close filter on outside click ── */
  useEffect(() => {
    const handle = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target))
        setShowFilterDropdown(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  /* ── Fetch sprints ── */
  useEffect(() => {
    const fetch_ = async () => {
      try {
        const res = await getAllSprints(token);
        if (res.status === 200) setSprints([...res.data].sort((a, b) => a.id - b.id));
      } catch (e) { console.error(e); }
    };
    if (token) fetch_();
  }, [token, refreshSprintsKey]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await getAllUsers(token);
        if (res?.status === 200) setAllUsers(res.data || []);
      } catch (e) { console.error(e); }
    };
    if (token) fetchUsers();
  }, [token]);

  useEffect(() => {
    const handler = async () => {
      try {
        const res = await getAllSprints(token);
        if (res.status === 200) setSprints(res.data);
      } catch (e) { console.error(e); }
    };
    window.addEventListener("sprintListUpdated", handler);
    return () => window.removeEventListener("sprintListUpdated", handler);
  }, [token]);

  /* ── Fetch backlog stories ── */
const fetchBacklogStories = useCallback(async () => {
  setLoading(true);

  try {
    // Fetch all stories
    const res = await getAllStory(token);

    const filtered = (res.data || []).filter((story) => {
      // Story has no sprint assigned
      const isSprintNull = story.sprint == null;

      // Story belongs to Backlog sprint
      const isBacklogSprint =
        story.sprint &&
        (
          story.sprint.sprintName?.toLowerCase() === "backlog" ||
          (story.sprint.startDate === null &&
            story.sprint.endDate === null)
        );

      return (
        isSprintNull ||
        story.status === "BACKLOG" ||
        isBacklogSprint
      );
    });

    // Add assigned user details
    const enriched = filtered.map((story) => {
      const full = { ...story };

      if (!full.assignedUser && full.assignedUserId) {
        const found = allUsers.find(
          (u) => u.id === Number(full.assignedUserId)
        );

        if (found) {
          full.assignedUser = found;
        }
      }

      return full;
    });

        const sorted = [...enriched].sort((a, b) => a.id - b.id);

    setStories(enriched);
  } catch (err) {
    console.error(err);
  } finally {
    setLoading(false);
  }
}, [token, allUsers]);

  useEffect(() => { if (!showViewTaskModal) fetchBacklogStories(); }, [showViewTaskModal, refreshKey, fetchBacklogStories]);
  useEffect(() => { fetchBacklogStories(); }, [fetchBacklogStories]);

  /* ── Fetch sprint stories ── */
  const fetchSprintStories = useCallback(async () => {
    try {
      const res = await getAllStory(token);
      const grouped = {};
      (res.data || []).forEach((story) => {
        if (story.sprint?.id && story.status !== "BACKLOG") {
          if (!grouped[story.sprint.id]) grouped[story.sprint.id] = [];
          grouped[story.sprint.id].push(story);
        }
      });
      setSprintStories(grouped);
    } catch (err) { console.error(err); }
  }, [token]);

  useEffect(() => { fetchSprintStories(); }, [fetchSprintStories]);

  useEffect(() => {
    const fetch_ = async () => {
      try { const res = await getAllStory(token); setStory(res.data || []); }
      catch (e) { console.error(e); }
    };
    fetch_();
  }, [token]);

  /* ── Handlers ── */
  const handleStoryClick = (story) => {
    if (!isAdmin && !isTeamLead) return;
    setCurrentStory({
      ...story,
      attachments: Array.isArray(story.attachments)
        ? story.attachments.map((a) => ({ id: a.id, fileName: a.fileName, filePath: a.filePath, fileUrl: a.fileUrl, isNew: false }))
        : [],
    });
    setShowViewTaskModal(true);
  };

  const toggleExpand = (id) =>
    setExpandedSprints((prev) => ({ ...prev, [id]: !prev[id] }));

 /* ── Seed shared order from DB once stories load (survives logout/new device) ── */
  useEffect(() => {
    if (stories.length && backlogOrder.length === 0) {
      const initialOrder = [...stories]
        .sort((a, b) => (a.backlogOrder ?? a.id) - (b.backlogOrder ?? b.id))
        .map((s) => s.id);
      setBacklogOrder(initialOrder);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stories]);

  useEffect(() => {
  return () => {
    document.getElementById("touch-drag-ghost")?.remove();
  };
}, []);


  /* ── Derived ── */
 const filteredStories = stories.filter((story) => {
    const matchSearch = !searchText ||
      story.storyTitle?.toLowerCase().includes(searchText.toLowerCase()) ||
      String(story.id).includes(searchText);
    const matchFilter =
      activeFilter === "All" ||
      (activeFilter === "Tasks"    && story.storyType === "TASK") ||
      (activeFilter === "Feedback" && story.storyType === "FEEDBACK");
    return matchSearch && matchFilter;
  }).sort((a, b) => {
    const idxA = backlogOrder.indexOf(a.id);
    const idxB = backlogOrder.indexOf(b.id);
    if (idxA === -1 && idxB === -1) return a.id - b.id;
    if (idxA === -1) return 1;
    if (idxB === -1) return -1;
    return idxA - idxB;
  });

const handleReorderDrop = (targetStoryId) => async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const draggedId = Number(e.dataTransfer.getData("storyId"));
    if (!draggedId || draggedId === targetStoryId) return;

    // Whether the cursor was over the top half or bottom half of the target
    // row — top half inserts BEFORE the target (existing behavior), bottom
    // half inserts AFTER it. This is what allows dropping onto the last row
    // to actually land the task last, instead of always landing above it.
    const rect = e.currentTarget.getBoundingClientRect();
    const dropAfter = (e.clientY - rect.top) > rect.height / 2;

    const currentIds = filteredStories.map((s) => s.id);

    // Dragged task isn't already in the backlog (e.g. dropped from a sprint
    // onto a full backlog row) — move it to backlog instead of reordering.
    if (!currentIds.includes(draggedId)) {
  try {
    // Insert the dragged task into the backlog order at the exact
    // position it was dropped on, instead of leaving it to fall back
    // to id-based sorting once it re-appears in the fetched list.
    const targetIndex = currentIds.indexOf(targetStoryId);
    const newOrder = [...currentIds];
    if (targetIndex === -1) {
      newOrder.push(draggedId);
    } else {
      newOrder.splice(dropAfter ? targetIndex + 1 : targetIndex, 0, draggedId);
    }
    persistBacklogOrder(newOrder);

    await moveStoryToBacklog(draggedId, token);
    toast.success("Task moved to Backlog");
    await fetchBacklogStories();
    await fetchSprintStories();
  } catch {
    toast.error("Failed to move task to backlog.");
  }
  return;
}

    const withoutDragged = currentIds.filter((id) => id !== draggedId);
    const targetIndex = withoutDragged.indexOf(targetStoryId);
    if (targetIndex === -1) return;
    withoutDragged.splice(dropAfter ? targetIndex + 1 : targetIndex, 0, draggedId);

    persistBacklogOrder(withoutDragged);
  };

  const today_ = new Date(); today_.setHours(0, 0, 0, 0);
 const plannedSprints = sprints.filter((sprint) => {
  const status = sprint.sprintStatus;
  const isBacklog = sprint.sprintName?.toLowerCase() === "backlog" ||
    (sprint.startDate === null && sprint.endDate === null);
  const isPlanned = status === "PLANNED" || status === null || status === undefined;
  return isPlanned && !isBacklog;
});


  const totalPages      = Math.ceil(plannedSprints.length / sprintsPerPage);
  const paginatedSprints = plannedSprints.slice(
    (currentPage - 1) * sprintsPerPage,
    currentPage * sprintsPerPage,
  );

  /* ── Stats data ── */
 const sprintTotalPoints = plannedSprints.reduce((total, sprint) => {
    const tasks = sprintStories[sprint.id] || [];
return total + tasks.reduce((s, x) => s + (Number(x.storyPoints) || 0) + (Number(x.testStoryPoints) || 0), 0);
  }, 0);

  const stats = [
    { label: "BACKLOG TASKS",  value: stories.length, icon: <IconStack /> },
    { label: "STORY POINTS",   value: sprintTotalPoints || "--", icon: <IconSpeed /> },
    { label: "SPRINTS",        value: sprints.length, icon: <IconBull  /> },
    { label: "STORY CAPACITY", value: "--", icon: <IconList /> },
  ];

  /* ════════════ RENDER ════════════ */
  return (
    <div style={{ fontFamily: "inherit", height: isMobile ? undefined : "calc(100vh - 156px)", display: "flex", flexDirection: "column" }}>

      {/* ── Top bar ── */}
      <div className="pb-topbar" style={{ flexWrap: "wrap", rowGap: 8 }}>
        {/* Search */}
        {/* width stays inline — different on mobile vs desktop */}
<div className="pb-search-box" style={{ width: "100%", maxWidth: isMobile ? 220 : 260, minWidth: 100, flex: "1 1 150px" }}>
              <IconSearch />
          <input
            className="pb-search-input" style={{ marginLeft: 6 }}
            type="text" placeholder="Search tasks..."
            value={searchText} onChange={(e) => setSearchText(e.target.value)}
          />
        </div>

        {/* Filter */}
        <div ref={filterRef} style={{ position: "relative", flexShrink: 0 }}>
          <button className="pb-filter-btn" onClick={() => setShowFilterDropdown((p) => !p)}>
            <IconFilter />
            {activeFilter === "All" ? "All" : activeFilter}
            <IconChevDown />
            {activeFilter !== "All" && <span className="pb-filter-badge">1</span>}
          </button>

          {showFilterDropdown && (
            <div className="pb-filter-dropdown">
              {["All", "Tasks", "Feedback"].map((f) => (
                <div
                  key={f}
                  className={`pb-filter-item ${activeFilter === f ? "pb-filter-active" : "pb-filter-inactive"}`}
                  onClick={() => { setActiveFilter(f); setShowFilterDropdown(false); }}
                >
                  {activeFilter === f ? <IconCheck /> : <span style={{ width: 12 }} />}
                  {f}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Buttons — desktop only */}
        {(isAdmin || isTeamLead) && !isMobile && (
  <button className="pb-btn-primary" onClick={() => setShowCreateTask(true)}>
    <IconPlus /> Create Task
  </button>
)}
{isAdmin && !isMobile && (
  <button className="pb-btn-secondary" onClick={() => setShowCreateSprint(true)}>
    <IconPlus color="#374151" /> Create Sprint
  </button>
)}
      </div>

      {/* ── Main layout ── */}
      <div className="pb-layout" style={{ flexDirection: isMobile ? "column" : "row", flexWrap: "wrap", gap: 16, flex: 1, minHeight: 0 }}>
        {/* ── LEFT: Backlog list ── */}
        <div
          className="pb-backlog-panel"
          data-backlog-panel="true"
                  style={{
            flex: isMobile ? "1 1 100%" : "1 1 30%",
            minWidth: isMobile ? "100%" : 280,
            maxWidth: isMobile ? "100%" : 420,
            width: isMobile ? "100%" : undefined,
            maxHeight: isMobile ? "45vh" : undefined,
            height: isMobile ? undefined : "100%",
            display: isMobile ? undefined : "flex",
            flexDirection: isMobile ? undefined : "column",
            overflowY: isMobile ? "auto" : "hidden",
          }}
          onDragOver={(e) => e.preventDefault()}
         onDrop={async (e) => {
            e.preventDefault();
            const storyId = e.dataTransfer.getData("storyId");
            if (!storyId) return;
            const draggedId = Number(storyId);
            try {
              // Give the returning story a sequence position — append it to
              // the end of the current backlog order instead of leaving it null.
              const newOrder = [...backlogOrder.filter((id) => id !== draggedId), draggedId];
              persistBacklogOrder(newOrder);

              await moveStoryToBacklog(draggedId, token);
              toast.success("Task moved to Backlog");
              fetchBacklogStories(); fetchSprintStories();
            } catch { toast.error("Failed to move task to backlog."); }
          }}
        >
          <div className="pb-panel-header">
            <div className="flex-gap-4" style={{ gap: 8 }}>
              {/* Stack icon */}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2">
                <polygon points="12 2 2 7 12 12 22 7 12 2"/>
                <polyline points="2 17 12 22 22 17"/>
                <polyline points="2 12 12 17 22 12"/>
              </svg>
              <span className="pb-panel-title">Backlog</span>
              <span className="pb-count-badge">{String(filteredStories.length).padStart(2, "0")}</span>
            </div>
          </div>

          {loading && <div className="pb-empty-msg">Loading backlog stories...</div>}
          {!loading && filteredStories.length === 0 && (
            <div className="pb-empty-msg" style={{ color: "#9ca3af" }}>No backlog stories available</div>
          )}

        <div
            ref={backlogScrollRef}
style={!isMobile ? { flex: 1, minHeight: 0, overflowY: "auto", paddingBottom: 12 } : { paddingBottom: 12 }}
            onDragOver={(e) => {
              if (isMobile) return;
              const container = backlogScrollRef.current;
              if (!container) return;
              const rect = container.getBoundingClientRect();
              const edge = 40;   // px from top/bottom that triggers scrolling
              const speed = 12;  // px scrolled per dragover tick
              if (e.clientY - rect.top < edge) {
                container.scrollTop -= speed;
              } else if (rect.bottom - e.clientY < edge) {
                container.scrollTop += speed;
              }
            }}
          >
            {filteredStories.map((story, idx) => {
              const typeLabel     = toLabel(story.storyType) || "Feature";
              const priority      = story.priority || story.storyPriority || null;
              const priorityLabel = toLabel(priority) || "Medium";
              const assigneeUser  = story.assignedUser || story.storyUsers?.[0]?.user || null;
              const assigneeInitials = assigneeUser
                ? (assigneeUser.firstName?.[0] || "") + (assigneeUser.lastName?.[0] || "")
                : "";

              return (
                <div
                  key={story.id}
                 className={`pb-story-row ${idx < filteredStories.length - 1 ? "pb-story-divider" : ""}`}
style={{ cursor: canDragTasks ? "grab" : "default", touchAction: canDragTasks ? "none" : "auto" }}
data-story-id={String(story.id)}
                  onClick={() => handleStoryClick(story)}
                 draggable={canDragTasks}
onDragStart={(e) => e.dataTransfer.setData("storyId", String(story.id))}
onDragOver={canDragTasks ? (e) => e.preventDefault() : undefined}
onDrop={canDragTasks ? handleReorderDrop(story.id) : undefined}
onTouchStart={(e) => {
  if (!canDragTasks) return;
  document.getElementById("touch-drag-ghost")?.remove();
  e.currentTarget._touchStoryId = String(story.id);
  const touch = e.touches[0];
  const clone = e.currentTarget.cloneNode(true);
  clone.id = "touch-drag-ghost";
  clone.style.cssText = `position:fixed;top:${touch.clientY - 30}px;left:${touch.clientX - 60}px;width:200px;opacity:0.7;z-index:9999;pointer-events:none;border-radius:8px;background:#fff;border:2px solid #1d4ed8;padding:8px;font-size:12px;`;
  document.body.appendChild(clone);
}}
onTouchMove={(e) => {
  if (!canDragTasks) return;
  e.preventDefault();
  const touch = e.touches[0];
  const ghost = document.getElementById("touch-drag-ghost");
  if (ghost) {
    ghost.style.top = `${touch.clientY - 30}px`;
    ghost.style.left = `${touch.clientX - 60}px`;
  }
     // auto-scroll the sprint column's own scrollable container (it caps at
    // 55vh on mobile, so window scrolling alone never reaches lower sprints)
    const sprintColEl = sprintColRef.current;
    if (sprintColEl) {
      const rect = sprintColEl.getBoundingClientRect();
      if (touch.clientY > rect.top && touch.clientY < rect.bottom) {
        if (touch.clientY - rect.top < 40) sprintColEl.scrollTop -= 10;
        else if (rect.bottom - touch.clientY < 40) sprintColEl.scrollTop += 10;
      }
    }
   // auto-scroll page when dragging near top/bottom edge (lets you reach
    // the Backlog panel or other sprint cards while dragging from a sprint)
    const h = window.innerHeight;
    if (touch.clientY < 100) window.scrollBy(0, -10);
    else if (touch.clientY > h - 100) window.scrollBy(0, 10);
  // highlight sprint under finger
  const el = document.elementFromPoint(touch.clientX, touch.clientY);
  const sprintCard = el?.closest("[data-sprint-id]");
  setDragOverSprintId(sprintCard ? Number(sprintCard.getAttribute("data-sprint-id")) : null);
}}
onTouchEnd={async (e) => {
  if (!canDragTasks) return;
  const ghost = document.getElementById("touch-drag-ghost");
  if (ghost) ghost.remove();
  const touch = e.changedTouches[0];
  const storyId = e.currentTarget._touchStoryId;
  if (!storyId) return;
  const el = document.elementFromPoint(touch.clientX, touch.clientY);
  const sprintCard = el?.closest("[data-sprint-id]");
  const sprintId = sprintCard ? Number(sprintCard.getAttribute("data-sprint-id")) : null;
  setDragOverSprintId(null);

  if (sprintId) {
    try {
      await assignStoryToSprint(Number(storyId), sprintId, token);
      persistBacklogOrder(backlogOrder.filter((id) => id !== Number(storyId)));
      toast.success("Task moved to sprint!");
      fetchBacklogStories(); fetchSprintStories();
    } catch { toast.error("Failed to move task."); }
    return;
  }

  // Not dropped on a sprint — check if dropped on another backlog row to reorder
  const targetRow = el?.closest("[data-story-id]");
  const targetStoryId = targetRow ? Number(targetRow.getAttribute("data-story-id")) : null;
  if (targetStoryId && targetStoryId !== Number(storyId)) {
    const rect = targetRow.getBoundingClientRect();
    const dropAfter = (touch.clientY - rect.top) > rect.height / 2;
    const currentIds = filteredStories.map((s) => s.id);
    const withoutDragged = currentIds.filter((id) => id !== Number(storyId));
    const targetIndex = withoutDragged.indexOf(targetStoryId);
    if (targetIndex !== -1) {
      withoutDragged.splice(dropAfter ? targetIndex + 1 : targetIndex, 0, Number(storyId));
      persistBacklogOrder(withoutDragged);
    }
  }
}}
                  onMouseEnter={(e) => e.currentTarget.style.background = "#e3e6ea"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                >
                  {/* Priority accent bar — color must stay inline (dynamic) */}
                  <div className="pb-priority-bar" style={{ background: getPriorityColor(priorityLabel) }} />

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="flex-between" style={{ marginBottom: 4 }}>
                      <span className="pb-story-id">#{story.id}</span>
                      <button className="pb-dots-btn">
                        <span className="pb-dot" /><span className="pb-dot" /><span className="pb-dot" />
                      </button>
                    </div>

                    <div className="pb-story-title">{story.storyTitle}</div>

                  <div className="flex-center" style={{ gap: 6, flexWrap: isMobile ? "wrap" : "nowrap" }}>
                      {/* type badge — background/color from map, stays inline */}
                      <span className="pb-badge" style={getTypeBadgeStyle(typeLabel)}>{typeLabel}</span>
                      {/* priority badge — background/color from map, stays inline */}
                      <span className="pb-priority-badge"
                        style={{ background: PRIORITY_BG[priorityLabel] || "#e8f5e9", color: getPriorityColor(priorityLabel) }}>
                        {priorityLabel}
                      </span>
{(Number(story.storyPoints) > 0 || Number(story.testStoryPoints) > 0) && <span className="pb-sp-text">{(Number(story.storyPoints) || 0) + (Number(story.testStoryPoints) || 0)} SP</span>}
                      {assigneeInitials && (
                        <span className="pb-avatar"
                          title={`${assigneeUser?.firstName || ""} ${assigneeUser?.lastName || ""}`.trim()}>
                          {assigneeInitials.toUpperCase()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── RIGHT: Stats + Sprint cards ── */}
       <div
          className="pb-layout-col"
          ref={sprintColRef}
          style={{
            flex: isMobile ? "1 1 100%" : "1 1 65%",
            minWidth: isMobile ? "100%" : 300,
            width: isMobile ? "100%" : undefined,
            maxHeight: isMobile ? "55vh" : undefined,
            height: isMobile ? undefined : "100%",
            overflowY: isMobile ? "auto" : "auto",
            paddingBottom: isMobile ? 110 : undefined,
          }}
        >
          {/* Stats grid — columns change mobile/desktop so stays inline */}
<div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(150px, 100%), 1fr))", gap: 12 }}>
              {stats.map(({ label, value, icon }) => (
              <div key={label} className="pb-stat-card">
                <div className="flex-center" style={{ gap: 8, marginBottom: 6 }}>
                  <div className="pb-stat-icon">{icon}</div>
                  <div className="pb-stat-label">{label}</div>
                </div>
                <div className="pb-stat-value">{value}</div>
              </div>
            ))}
          </div>

          {/* Sprint cards */}
          {paginatedSprints.map((sprint) => {
            const now   = new Date(); now.setHours(0, 0, 0, 0);
            const start = new Date(sprint.startDate); start.setHours(0, 0, 0, 0);
            const end   = new Date(sprint.endDate);   end.setHours(23, 59, 59, 999);
            const statusLabel = sprint.sprintStatus === "ACTIVE" ? "Active"
  : sprint.sprintStatus === "COMPLETED" ? "Completed" : "Planned";
const statusStyle = {
  background: sprint.sprintStatus === "ACTIVE" ? "#dcfce7"
    : sprint.sprintStatus === "COMPLETED" ? "#fef9c3" : "#eff6ff",
  color: sprint.sprintStatus === "ACTIVE" ? "#166534"
    : sprint.sprintStatus === "COMPLETED" ? "#854d0e" : "#1d4ed8",
};
            const isOver   = dragOverSprintId === sprint.id;
            const expanded = expandedSprints[sprint.id] !== false;
            const tasks    = sprintStories[sprint.id] || [];
            const page     = sprintTaskPage[sprint.id] || 1;
            const totalTask  = tasks.length;
            const totalPagesTask = Math.ceil(totalTask / tasksPerSprintPage);
            const paginated  = tasks.slice((page - 1) * tasksPerSprintPage, page * tasksPerSprintPage);

            return (
              <div
                key={sprint.id}
                className={`pb-sprint-card ${isOver ? "pb-sprint-card-over" : ""}`}
                onDragOver={(e) => { e.preventDefault(); setDragOverSprintId(sprint.id); }}
                data-sprint-id={sprint.id}
onDragOver={(e) => { e.preventDefault(); setDragOverSprintId(sprint.id); }}
onDragLeave={() => setDragOverSprintId(null)}
onDrop={async (e) => {
  e.preventDefault(); setDragOverSprintId(null);
  const storyId = e.dataTransfer.getData("storyId");
  if (!storyId) return;
  try {
    await assignStoryToSprint(Number(storyId), sprint.id, token);
    persistBacklogOrder(backlogOrder.filter((id) => id !== Number(storyId)));
    toast.success("Task moved to " + sprint.sprintName);
    fetchBacklogStories(); fetchSprintStories();
  } catch { toast.error("Failed to move task."); }
}}
onTouchEnd={async (e) => {
  if (!isMobile) return;
  // find which story card the touch started on
  const touch = e.changedTouches[0];
  const el = document.elementFromPoint(touch.clientX, touch.clientY);
  const storyRow = el?.closest("[data-story-id]");
  const storyId = storyRow?.getAttribute("data-story-id");
  if (!storyId) return;
  setDragOverSprintId(null);
  try {
    await assignStoryToSprint(Number(storyId), sprint.id, token);
    persistBacklogOrder(backlogOrder.filter((id) => id !== Number(storyId)));
    toast.success("Task moved to " + sprint.sprintName);
    fetchBacklogStories(); fetchSprintStories();
  } catch { toast.error("Failed to move task."); }
}}
              >
                <div className="pb-sprint-header">
                  <div onClick={(e) => { e.stopPropagation(); toggleExpand(sprint.id); }} style={{ cursor: "pointer", display: "flex", alignItems: "center" }}>
                    <IconChevRight expanded={expanded} />
                  </div>

                  <div style={{ flexShrink: 0 }}>
                    <div className="flex-center" style={{ gap: 8 }}>
                      <span className="pb-sprint-name">{sprint.sprintName}</span>
                      <span className="pb-sprint-status" style={statusStyle}>{statusLabel}</span>
                    </div>
                    <div className="pb-sprint-dates">
                      <IconCalendar />
                      {formatDate(sprint.startDate)} – {formatDate(sprint.endDate)}
                    </div>
                  </div>

                  <div className="pb-sprint-right">
                    <span className="pb-sprint-stat">• Tasks <strong>{tasks.length}</strong></span>
<span className="pb-sprint-stat">• Story Points <strong>{tasks.reduce((s, x) => s + (Number(x.storyPoints) || 0) + (Number(x.testStoryPoints) || 0), 0)}</strong></span>
                    {isAdmin && (
                      <>
{(() => {
  const today = new Date(); today.setHours(0, 0, 0, 0);
const sprintStart = new Date(sprint.startDate); sprintStart.setHours(0, 0, 0, 0);
const hasActiveSprint = sprints.some(s => s.sprintStatus === "ACTIVE");
const canStart = !hasActiveSprint && sprintStart <= today;
const titleMsg = hasActiveSprint
  ? "Another sprint is already active"
  : sprintStart > today
  ? `Sprint starts on ${formatDate(sprint.startDate)}`
  : "Start Sprint";
  return (
    <button
      className="pb-sprint-start-btn"
      disabled={!canStart}
      title={titleMsg}
      onClick={(e) => { e.stopPropagation(); if (canStart) setStartingSprint(sprint); }}
      style={{ opacity: canStart ? 1 : 0.4, cursor: canStart ? "pointer" : "not-allowed" }}
    >
      <IconPlay /> Start
    </button>
  );
})()}                      <button className="pb-icon-btn" onClick={() => { setEditingSprint(sprint); setShowEditSprint(true); }}>
                          <IconEdit />
                        </button>
                        <button className="pb-icon-btn" onClick={() => setDeletingSprintId(sprint.id)}>
                          <IconTrash />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Drop zone (collapsed) */}
                {!expanded && (
                  <div className="pb-drop-zone">
                    {tasks.length > 0 ? `${tasks.length} task(s) — click > to expand` : "Drop tasks here"}
                  </div>
                )}

                {/* Task list (expanded) */}
                {expanded && (
                  <div style={{ padding: "0 16px 12px", display: "flex", flexDirection: "column", gap: 6 }}>
                    {tasks.length > 0 ? (
                      <>
                        {paginated.map((s) => {
                          const p   = toLabel(s.priority || s.storyPriority || "Medium");
                          const typ = toLabel(s.storyType) || "Feature";
                          return (
<div
  key={s.id}
  className="pb-task-row"
  style={{ cursor: "grab", touchAction: "none" }}
  data-story-id={String(s.id)}
  onClick={() => handleStoryClick(s)}
  draggable
  onDragStart={(e) => e.dataTransfer.setData("storyId", String(s.id))}
  onTouchStart={(e) => {
    document.getElementById("touch-drag-ghost")?.remove();
    e.currentTarget._touchStoryId = String(s.id);
    const touch = e.touches[0];
    const clone = e.currentTarget.cloneNode(true);
    clone.id = "touch-drag-ghost";
    clone.style.cssText = `position:fixed;top:${touch.clientY - 30}px;left:${touch.clientX - 60}px;width:200px;opacity:0.7;z-index:9999;pointer-events:none;border-radius:8px;background:#fff;border:2px solid #1d4ed8;padding:8px;font-size:12px;`;
    document.body.appendChild(clone);
  }}
  onTouchMove={(e) => {
    e.preventDefault();
    const touch = e.touches[0];
    const ghost = document.getElementById("touch-drag-ghost");
    if (ghost) {
      ghost.style.top = `${touch.clientY - 30}px`;
      ghost.style.left = `${touch.clientX - 60}px`;
    }
    // auto-scroll page when dragging near top/bottom edge (lets you reach
    // the Backlog panel or other sprint cards while dragging from a sprint)
    const h = window.innerHeight;
    if (touch.clientY < 100) window.scrollBy(0, -10);
    else if (touch.clientY > h - 100) window.scrollBy(0, 10);
    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    const sprintCard = el?.closest("[data-sprint-id]");
    const targetSprintId = sprintCard ? Number(sprintCard.getAttribute("data-sprint-id")) : null;
    setDragOverSprintId(targetSprintId && targetSprintId !== sprint.id ? targetSprintId : null);
  }}

onTouchEnd={async (e) => {
    e.stopPropagation();
    const ghost = document.getElementById("touch-drag-ghost");
    if (ghost) ghost.remove();
    const touch = e.changedTouches[0];
    const storyId = e.currentTarget._touchStoryId;
    setDragOverSprintId(null);
    if (!storyId) return;
    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    const backlogPanel = el?.closest("[data-backlog-panel]");
    const sprintCard = el?.closest("[data-sprint-id]");
    const targetSprintId = sprintCard ? Number(sprintCard.getAttribute("data-sprint-id")) : null;
    try {
      if (backlogPanel) {
        persistBacklogOrder([...backlogOrder.filter((id) => id !== Number(storyId)), Number(storyId)]);
        await moveStoryToBacklog(Number(storyId), token);
        toast.success("Task moved to Backlog");
        fetchBacklogStories(); fetchSprintStories();
      } else if (targetSprintId && targetSprintId !== sprint.id) {
        await assignStoryToSprint(Number(storyId), targetSprintId, token);
        toast.success("Task moved to sprint!");
        fetchBacklogStories(); fetchSprintStories();
      }
    } catch { toast.error("Failed to move task."); }
  }}
>
                                <div className="pb-task-bar" style={{ background: getPriorityColor(p) }} />
                              <span className="pb-task-id">#{s.id}</span>
                              <span className="pb-task-title">{s.storyTitle}</span>
                              <span className="pb-badge" style={getTypeBadgeStyle(typ)}>{typ}</span>
                              <span className="pb-priority-badge"
                                style={{ background: PRIORITY_BG[p] || "#e8f5e9", color: getPriorityColor(p) }}>
                                {p}
                              </span>
{(Number(s.storyPoints) > 0 || Number(s.testStoryPoints) > 0) && (
  <span className="pb-task-sp">
    {(Number(s.storyPoints) || 0) + (Number(s.testStoryPoints) || 0)} SP
  </span>
)}                            </div>
                          );
                        })}

                        {totalPagesTask > 1 && (
                          <div className="pb-task-pager">
                            <span className="pb-task-pager-text">
                              {(page - 1) * tasksPerSprintPage + 1}–{Math.min(page * tasksPerSprintPage, totalTask)} of {totalTask}
                            </span>
                            <button className="pb-pager-btn" disabled={page === 1}
                              onClick={() => setSprintTaskPage((prev) => ({ ...prev, [sprint.id]: Math.max(1, (prev[sprint.id] || 1) - 1) }))}>
                              ‹
                            </button>
                            <button className="pb-pager-btn" disabled={page === totalPagesTask}
                              onClick={() => setSprintTaskPage((prev) => ({ ...prev, [sprint.id]: Math.min(totalPagesTask, (prev[sprint.id] || 1) + 1) }))}>
                              ›
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="pb-drop-zone">Drop tasks here</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Sprint list pagination */}
          {plannedSprints.length > sprintsPerPage && (
            <div className="pb-sprint-pager">
              <span className="pb-task-pager-text">
                {(currentPage - 1) * sprintsPerPage + 1}–{Math.min(currentPage * sprintsPerPage, plannedSprints.length)} of {plannedSprints.length}
              </span>
              <button className="pb-pager-btn" disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>‹</button>
              <button className="pb-pager-btn" disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}>›</button>
            </div>
          )}

         {/* Create new sprint placeholder */}
          {isAdmin && (
            <div className="pb-create-sprint-btn" onClick={() => setShowCreateSprint(true)}>
              <IconPlus color="currentColor" /> Create New Sprint
            </div>
          )}
        </div>
      </div>

      {/* ── Mobile FAB ── */}
      {(isAdmin || isTeamLead) && isMobile && (
  <div className="pb-fab-wrap">
    <button className="pb-fab-btn pb-fab-task" onClick={() => setShowCreateTask(true)}>
      <IconPlus /> Task
    </button>
    {isAdmin && (
      <button className="pb-fab-btn pb-fab-sprint" onClick={() => setShowCreateSprint(true)}>
        <IconPlus /> Sprint
      </button>
    )}
  </div>
)}

      {/* ── Modals ── */}
      <CreateTaskModal
        show={showCreateTask}
        onClose={() => setShowCreateTask(false)}
        token={token}
        onSave={() => { setShowCreateTask(false); fetchBacklogStories(); }}
      />

      <CreateSprintModal
  show={showCreateSprint}
  onClose={() => setShowCreateSprint(false)}
  token={token}
  sprints={sprints}
  onSprintCreated={() => {
    setRefreshSprintsKey((k) => k + 1);
    window.dispatchEvent(new Event("sprintListUpdated"));
  }}
/>

      {showViewTaskModal && (
        <TaskDetails
          showViewTaskModal={showViewTaskModal}
          setShowViewTaskModal={setShowViewTaskModal}
          currentStory={currentStory}
          setCurrentStory={setCurrentStory}
onStoryUpdated={() => { fetchBacklogStories(); fetchSprintStories(); }}        />
      )}

      <EditSprintModal
        show={showEditSprint}
        onClose={() => { setShowEditSprint(false); setEditingSprint(null); }}
        token={token}
        sprint={editingSprint}
        onUpdated={() => {
          setRefreshSprintsKey((k) => k + 1);
          window.dispatchEvent(new Event("sprintListUpdated"));
        }}
      />

      <DeleteSprintModal
        sprintId={deletingSprintId}
        onClose={() => setDeletingSprintId(null)}
        token={token}
        onDeleted={async () => {
          setRefreshSprintsKey((k) => k + 1);
          await new Promise((r) => setTimeout(r, 500));
          await fetchBacklogStories();
          await fetchSprintStories();
          window.dispatchEvent(new Event("sprintListUpdated"));
        }}
      />

      <StartSprintModal
        sprint={startingSprint}
        tasks={startingSprint ? (sprintStories[startingSprint.id] || []) : []}
        onClose={() => setStartingSprint(null)}
        token={token}
        onStarted={async () => {
          const res = await getAllSprints(token);
          if (res.status === 200) setSprints([...res.data].sort((a, b) => a.id - b.id));
          setRefreshSprintsKey((k) => k + 1);
          fetchSprintStories();
          window.dispatchEvent(new Event("sprintListUpdated"));
        }}
      />

      <ToastContainer />
    </div>
  );
};

export default PlanningBacklogTab;
